import Image from "next/image";
import Link from "next/link";
import { canOptimizeExploreImage } from "../_lib/explore-image";
import type { ExploreStoryCard } from "../types";
import { STORY_BADGE_STYLES } from "./category-badge-styles";
import { HighlightedText } from "./highlighted-text";

const MATERIAL_TYPE_LABELS: Partial<
  Record<ExploreStoryCard["materialType"], string>
> = {
  PROMPT: "Prompt",
  WORKFLOW: "Workflow",
  RELEASE: "Release",
  DEAL: "Deal",
};

interface StoryGridCardProps {
  story: ExploreStoryCard;
  exploreBase: string;
  highlightQuery?: string;
}

export function StoryGridCard({
  story,
  exploreBase,
  highlightQuery,
}: StoryGridCardProps) {
  const primaryTag = story.productTags[0] ?? story.topicTags[0];
  const materialLabel = MATERIAL_TYPE_LABELS[story.materialType];
  const badgeStyleClass = STORY_BADGE_STYLES[story.storyBadge];
  const shouldOptimizeImage = story.coverImageUrl
    ? canOptimizeExploreImage({
        src: story.coverImageUrl,
      })
    : false;

  return (
    <Link
      className="hover:-translate-y-0.5 flex gap-4 rounded-2xl bg-[var(--explore-bg-card)] p-2 no-underline shadow-[var(--explore-card-shadow-lg)] transition-all hover:shadow-[var(--explore-card-shadow-lg-hover)] active:scale-[0.99] sm:gap-5 sm:p-2"
      href={`${exploreBase}/stories/${story.storyId}`}
      prefetch={false}
      style={{ fontFamily: "var(--homepage-sans-stack)" }}
    >
      {/* Thumbnail */}
      <div className="relative w-[120px] shrink-0 self-stretch overflow-hidden rounded-tl-xl rounded-bl-xl md:w-[200px]">
        {story.coverImageUrl ? (
          <Image
            alt={story.headline}
            className="h-full w-full object-cover"
            fill
            sizes="200px"
            src={story.coverImageUrl}
            unoptimized={!shouldOptimizeImage}
          />
        ) : (
          <div className="h-full w-full bg-gradient-to-br from-[var(--explore-bg)] to-[var(--explore-border)]" />
        )}
      </div>

      {/* Content */}
      <div className="flex min-w-0 flex-1 flex-col justify-center py-1 pr-2">
        <h3
          className="mb-1.5 line-clamp-2 font-bold text-[1.3rem] text-[var(--explore-text-primary)] leading-[1.3]"
          style={{ fontFamily: "var(--explore-card-title-stack)" }}
        >
          <HighlightedText query={highlightQuery} text={story.headline} />
        </h3>
        <p className="mb-2.5 line-clamp-2 text-[0.9rem] text-[var(--explore-text-secondary)] leading-[1.5]">
          <HighlightedText query={highlightQuery} text={story.summary} />
        </p>
        <div className="flex flex-wrap items-center gap-1.5">
          {materialLabel && (
            <span
              className={`inline-flex items-center rounded-sm px-2 py-0.5 font-bold text-[0.6rem] uppercase tracking-[0.05em] ${badgeStyleClass}`}
            >
              {materialLabel}
            </span>
          )}
          {story.categoryEmoji && (
            <span className="inline-flex items-center justify-center rounded-sm bg-[var(--explore-tag-bg)] px-1.5 py-0.5 text-[0.7rem]">
              {story.categoryEmoji}
            </span>
          )}
          {primaryTag && (
            <span className="inline-flex items-center rounded-sm bg-[var(--explore-tag-bg)] px-2 py-0.5 font-medium text-[0.7rem] text-[var(--explore-tag-text)]">
              {primaryTag.displayName}
            </span>
          )}
          <span className="text-[0.7rem] text-[var(--explore-text-muted)]">
            {story.timeAgo}
          </span>
        </div>
      </div>
    </Link>
  );
}
